import { useEffect, useMemo, useRef, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../../lib/supabaseClient';
import {
  fetchConfigCanjes, recursosParaCargarCanje, registrarCanjeManual, uploadCanjePhoto,
} from '../../api/canjes';
import type { ConfigCanjes, RecursoParaCarga } from '../../api/canjes';
import { describirRegla, etiquetaUnidad, premiosSugeridos, resumenPorUnidad } from '../../domain/canjes';
import {
  bloqueoDeCargaManual, lineaManualVacia, lineasParaGuardar, ocurridoEnDesde, resolverLineas,
  type LineaManual,
} from '../../domain/canjeManual';
import { compressImage } from '../../lib/compressImage';
import { rechazoDeArchivo } from '../../lib/archivoDeFoto';

const TZ = 'America/Lima';

interface CampaniaOpcion { id: string; client: string; brand: string }
interface PdvOpcion { id: string; code: string; name: string }

/**
 * El turno desde el que se abrió el formulario en la vista «Por turno» (0375). Con él, campaña, fecha, tienda y
 * persona vienen puestas y no se pueden cambiar: el canje es de ESE turno. Para otra combinación está el botón
 * general de la bandeja.
 */
export interface TurnoFijo {
  campaignId: string;
  fecha: string;
  pdvId: string;
  workerId: string;
  /** Lo que se lee arriba del formulario: «30-oct · Mercado Productores · Milen Ttica (10:00 a 17:00)». */
  descripcion: string;
}

/**
 * Cargar un canje a mano desde la bandeja (0374): lo que el campo no registró desde el celular.
 *
 * Es el contenido del modal; el marco lo pone la bandeja con su `Modal`, que es LA capa de modal
 * de la app. Después de guardar, el formulario NO se cierra: conserva campaña, fecha, tienda,
 * persona y motivo, y limpia solo lo del canje. Una regularización son decenas de canjes del mismo
 * día y la misma planilla, y volver a elegir las cinco cosas en cada uno es donde se cometen los
 * errores de carga.
 *
 * La persona sale de `recursos_para_cargar_canje`: solo quien tiene turno aprobado en la campaña.
 * Si trabajó sin estar asignada, primero se asigna (es lo que además la hace entrar a la planilla).
 */
export default function CargaManualCanje({ campanias, campaignIdInicial, fijo, onCargado, onCerrar }: {
  /** Solo las campañas donde quien mira puede cargar: el servidor rechaza las demás. */
  campanias: CampaniaOpcion[];
  campaignIdInicial: string;
  fijo?: TurnoFijo;
  /** Avisa cada canje guardado, para que la bandeja sepa que tiene que volver a leer. */
  onCargado: () => void;
  onCerrar: () => void;
}) {
  const hoy = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

  const [campaignId, setCampaignId] = useState(
    fijo?.campaignId
      ?? (campanias.some((c) => c.id === campaignIdInicial) ? campaignIdInicial
        : campanias.length === 1 ? campanias[0].id : ''),
  );
  const [fecha, setFecha] = useState(fijo?.fecha ?? hoy);
  const [pdvId, setPdvId] = useState(fijo?.pdvId ?? '');
  const [workerId, setWorkerId] = useState(fijo?.workerId ?? '');
  /**
   * La campaña con la que se montó. Cambiar de campaña limpia tienda y persona, pero MONTAR no es cambiar: sin esto,
   * el formulario abierto desde un turno borraba en el primer render la tienda y la persona que traía puestas.
   */
  const campaniaMontada = useRef(campaignId);

  const [pdvs, setPdvs] = useState<PdvOpcion[]>([]);
  const [recursos, setRecursos] = useState<RecursoParaCarga[]>([]);
  const [config, setConfig] = useState<ConfigCanjes | null>(null);
  const [errorDeLectura, setErrorDeLectura] = useState('');

  const [lineas, setLineas] = useState<LineaManual[]>([lineaManualVacia()]);
  const [hora, setHora] = useState('');
  const [cliente, setCliente] = useState('');
  const [contacto, setContacto] = useState('');
  const [monto, setMonto] = useState('');
  const [numeroBoleta, setNumeroBoleta] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [fotoBoleta, setFotoBoleta] = useState<File | null>(null);
  const [fotoPremio, setFotoPremio] = useState<File | null>(null);
  /** Para poder vaciar los `<input type="file">` después de guardar: se remontan con otra clave. */
  const [claveFotos, setClaveFotos] = useState(0);
  const [motivo, setMotivo] = useState('');

  /**
   * Un uuid por CANJE, no por intento: si la respuesta del servidor se pierde y se vuelve a pulsar
   * «Guardar», la RPC devuelve el mismo canje en vez de duplicarlo. Las fotos suben a una ruta
   * armada con él (`upsert`), así que reintentar tampoco deja copias huérfanas en el bucket.
   */
  const [clientUuid, setClientUuid] = useState(() => crypto.randomUUID());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [hecho, setHecho] = useState('');
  const [cargados, setCargados] = useState(0);

  // Tiendas de la campaña. Al cambiar de campaña se limpia todo lo que dependía de ella: una tienda
  // o una persona de otra campaña harían rebotar el canje sin que se vea por qué.
  useEffect(() => {
    if (campaignId !== campaniaMontada.current) {
      campaniaMontada.current = campaignId;
      setPdvId('');
      setWorkerId('');
    }
    setPdvs([]);
    if (campaignId === '') return;
    let vigente = true;
    void supabase
      .from('campaign_schedules')
      .select('pdv:pdv_id(id, code, name)')
      .eq('campaign_id', campaignId)
      .then(({ data, error: e }) => {
        if (!vigente) return;
        if (e) { setErrorDeLectura(`No se pudieron leer las tiendas: ${e.message}`); return; }
        const vistos = new Map<string, PdvOpcion>();
        for (const fila of (data ?? []) as unknown as { pdv: PdvOpcion | null }[]) {
          if (fila.pdv) vistos.set(fila.pdv.id, fila.pdv);
        }
        setPdvs([...vistos.values()].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      });
    return () => { vigente = false; };
  }, [campaignId]);

  // Personas y dinámicas dependen de campaña, tienda Y fecha: quién tenía turno ese día ahí, y qué
  // dinámicas valían ese día en esa tienda (0029). Se piden juntas y se descarta la respuesta vieja
  // si mientras tanto cambió alguna de las tres.
  useEffect(() => {
    setErrorDeLectura('');
    if (campaignId === '' || pdvId === '' || fecha === '') {
      setRecursos([]);
      setConfig(null);
      return;
    }
    let vigente = true;
    void Promise.all([
      recursosParaCargarCanje(campaignId, pdvId, fecha),
      fetchConfigCanjes(campaignId, pdvId, fecha),
    ]).then(([rs, cfg]) => {
      if (!vigente) return;
      setRecursos(rs);
      setWorkerId((w) => (rs.some((r) => r.workerId === w) ? w : ''));
      setConfig(cfg);
      // Se conservan las líneas cuya dinámica sigue valiendo; las demás se vacían en vez de viajar.
      setLineas((ls) => {
        const ids = new Set(cfg.dinamicas.map((d) => d.id));
        const quedan = ls.filter((l) => l.dynamicId === '' || ids.has(l.dynamicId));
        return quedan.length > 0 ? quedan : [lineaManualVacia()];
      });
    }).catch((e: unknown) => {
      if (!vigente) return;
      setErrorDeLectura(e instanceof Error ? e.message : String(e));
    });
    return () => { vigente = false; };
  }, [campaignId, pdvId, fecha]);

  const dinamicas = useMemo(() => config?.dinamicas ?? [], [config]);
  /** Premio por el total del canje (0386, Takis Mercados por tiras). Con ella no se pregunta premio por línea. */
  const regla = config?.premioPorTotal ?? null;
  const resueltas = useMemo(() => resolverLineas(lineas, dinamicas, regla), [lineas, dinamicas, regla]);
  const recurso = recursos.find((r) => r.workerId === workerId) ?? null;

  const bloqueo = bloqueoDeCargaManual({
    campaignId, fecha, hoy, pdvId, workerId, lineas, dinamicas,
    maxPerClient: config?.maxPerClient ?? null, motivo, regla,
  });
  const totalCanjes = resueltas.reduce((s, l) => s + l.canjes, 0);
  const totalPremios = resueltas.reduce((s, l) => s + l.premios.reduce((t, p) => t + p.units, 0), 0);
  const nombrePremio = (id: string) => config?.premios.find((p) => p.id === id)?.name ?? 'premio';
  /** «Tiras» o «Canjes»: el rótulo de la cantidad de cada línea y de los totales. */
  const unidad = etiquetaUnidad(config?.unidadPlural ?? null);
  const unidadDe = (id: string) => dinamicas.find((x) => x.id === id)?.unidad;
  // Con líneas de distinta unidad (tiras y cajas, 0407) no se suman entre sí: «6 tiras · 1 caja».
  const resumenDeCantidades = regla === null
    ? `${totalCanjes} canje(s) · ${totalPremios} premio(s)`
    : `${resumenPorUnidad(resueltas, unidadDe, unidad.toLowerCase())} · ${totalPremios} ${nombrePremio(regla.prizeId)}`;

  function cambiarLinea(i: number, cambio: Partial<LineaManual>) {
    setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...cambio } : l)));
    setHecho('');
  }

  function cambiarUnidades(i: number, prizeId: string, units: number) {
    setLineas((ls) => ls.map((l, j) => {
      if (j !== i) return l;
      const sinEse = (l.premiosManuales ?? []).filter((p) => p.prizeId !== prizeId);
      return { ...l, premiosManuales: units > 0 ? [...sinEse, { prizeId, units }] : sinEse };
    }));
  }

  function elegirFoto(cual: 'boleta' | 'premio', f: File | null) {
    const rechazo = f === null ? null : rechazoDeArchivo(f);
    if (rechazo !== null) { setError(rechazo); return; }
    setError('');
    if (cual === 'boleta') setFotoBoleta(f); else setFotoPremio(f);
  }

  async function guardar() {
    if (bloqueo !== null || config === null) return;
    setGuardando(true);
    setError('');
    setHecho('');
    try {
      // Las fotos van ANTES de la RPC: si la subida falla no queda un canje apuntando a la nada.
      const fotoBoletaPath = fotoBoleta === null ? null
        : await uploadCanjePhoto(clientUuid, 'boleta', await compressImage(fotoBoleta));
      const fotoPremioPath = fotoPremio === null ? null
        : await uploadCanjePhoto(clientUuid, 'premio', await compressImage(fotoPremio));
      const montoNumero = monto.trim() === '' ? null : Number(monto.replace(',', '.'));
      await registrarCanjeManual({
        clientUuid,
        campaignId,
        pdvId,
        workerId,
        fecha,
        ocurridoEn: ocurridoEnDesde(fecha, hora),
        clienteNombre: cliente.trim() === '' ? null : cliente.trim(),
        clienteContacto: contacto.trim() === '' ? null : contacto.trim(),
        montoCompra: montoNumero !== null && Number.isFinite(montoNumero) ? montoNumero : null,
        marcasDejadas: [],
        observaciones: observaciones.trim() === '' ? null : observaciones.trim(),
        fotoBoletaPath,
        boletaNumero: numeroBoleta.trim() === '' ? null : numeroBoleta.trim(),
        fotoPremioPath,
        lineas: lineasParaGuardar(resueltas, dinamicas, regla),
      }, motivo.trim());

      const n = cargados + 1;
      setCargados(n);
      onCargado();
      setHecho(
        `Canje cargado: ${resumenDeCantidades} a nombre de `
        + `${recurso?.nombre ?? 'la persona elegida'}. Van ${n} en esta carga. Se mantienen campaña, `
        + 'fecha, tienda, persona y motivo para el siguiente.',
      );
      setClientUuid(crypto.randomUUID());
      setLineas([lineaManualVacia()]);
      setHora('');
      setCliente('');
      setContacto('');
      setMonto('');
      setNumeroBoleta('');
      setObservaciones('');
      setFotoBoleta(null);
      setFotoPremio(null);
      setClaveFotos((k) => k + 1);
    } catch (e) {
      setError('No se guardó: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="text-sm">
      <p className="mb-3 text-gray-600">
        Para lo que el campo no registró desde el celular. El canje queda a nombre de la persona que
        lo hizo, marcado como <strong>cargado a mano</strong>, con tu nombre y el motivo. Cuenta
        igual que uno del celular en los tableros.
      </p>

      {fijo ? (
        <div className="grid grid-cols-2 gap-3">
          <p className="col-span-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-blue-900">
            Turno: <strong>{fijo.descripcion}</strong>
          </p>
          <label className="flex flex-col">
            Hora <span className="text-xs text-gray-500">(si se sabe)</span>
            <input type="time" className="rounded border px-2 py-1" value={hora}
              onChange={(e) => setHora(e.target.value)} />
          </label>
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 flex flex-col">
          Campaña
          <select className="rounded border px-2 py-1" value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">Elige la campaña</option>
            {campanias.map((c) => (
              <option key={c.id} value={c.id}>{c.client} — {c.brand}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          Fecha del canje
          <input type="date" className="rounded border px-2 py-1" value={fecha} max={hoy}
            onChange={(e) => setFecha(e.target.value)} />
        </label>

        <label className="flex flex-col">
          Hora <span className="text-xs text-gray-500">(si se sabe)</span>
          <input type="time" className="rounded border px-2 py-1" value={hora}
            onChange={(e) => setHora(e.target.value)} />
        </label>

        <label className="col-span-2 flex flex-col">
          Tienda
          <select className="rounded border px-2 py-1" value={pdvId}
            disabled={pdvs.length === 0} onChange={(e) => setPdvId(e.target.value)}>
            <option value="">{campaignId === '' ? 'Primero elige la campaña' : 'Elige la tienda'}</option>
            {pdvs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>

        {/* La ayuda va FUERA del `label`: adentro pasaba a ser parte del nombre del control, y un
            lector de pantalla leía tres oraciones para anunciar un desplegable. */}
        <div className="col-span-2 flex flex-col">
          <label htmlFor="carga-persona">Persona que hizo el canje</label>
          <select id="carga-persona" className="rounded border px-2 py-1" value={workerId}
            aria-describedby="carga-persona-ayuda"
            disabled={recursos.length === 0} onChange={(e) => setWorkerId(e.target.value)}>
            <option value="">
              {pdvId === '' ? 'Primero elige la tienda'
                : recursos.length === 0 ? 'Nadie tiene turno aprobado en la campaña' : 'Elige a la persona'}
            </option>
            {recursos.map((r) => (
              <option key={r.workerId} value={r.workerId}>
                {r.nombre}{r.programada ? ' · con turno ese día aquí' : ''}
              </option>
            ))}
          </select>
          <span id="carga-persona-ayuda" className="mt-0.5 text-xs text-gray-500">
            Salen las personas con algún turno aprobado en la campaña. Si falta alguien, asígnala
            primero en Asignaciones.
          </span>
          {recurso !== null && !recurso.programada && (
            <span className="mt-0.5 text-xs text-amber-700">
              No tenía turno aprobado en esta tienda ese día: el canje va a quedar marcado
              «fuera de programación».
            </span>
          )}
        </div>
      </div>
      )}

      {errorDeLectura !== '' && (
        <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          {errorDeLectura}
        </p>
      )}

      <section className="mt-4 rounded border p-3">
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="font-medium">
            {regla === null ? 'Canjes y premios'
              : regla.combos ? 'Lo vendido y el premio' : `${unidad} vendidas y premio`}
          </h3>
          {resueltas.length > 0 && (
            <span className="text-xs text-gray-600">
              {resumenDeCantidades}
              {config?.maxPerClient != null && ` · tope ${config.maxPerClient} por cliente`}
            </span>
          )}
        </div>
        {/* La regla se dice en pantalla: el premio lo calcula el sistema, y quien carga tiene que poder comprobarlo
            contra lo que se entregó en el puesto. */}
        {regla !== null && (
          <p className="mb-2 rounded bg-blue-50 px-2 py-1 text-xs text-blue-900">
            El premio se calcula solo: {describirRegla(regla, nombrePremio(regla.prizeId), unidad.toLowerCase())}
            {' '}Si no alcanza, se registra la venta sin premio.
          </p>
        )}
        {config === null ? (
          <p className="text-gray-500">Elige campaña, fecha y tienda para ver sus dinámicas.</p>
        ) : dinamicas.length === 0 ? (
          <p className="text-gray-700">Esta campaña no tiene dinámicas vigentes en esa tienda ese día.</p>
        ) : (
          <>
            {lineas.map((l, i) => {
              const d = dinamicas.find((x) => x.id === l.dynamicId);
              const eligePremio = d?.prizeSelection === 'elegido';
              const editaPremios = d !== undefined && (eligePremio || l.premiosManuales !== null);
              const sugeridos = d ? premiosSugeridos(d, l.canjes) : [];
              return (
                <div key={i} className="mb-3 border-b pb-3 last:mb-0 last:border-b-0 last:pb-0">
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="flex min-w-0 flex-1 flex-col">
                      Dinámica
                      <select className="rounded border px-2 py-1" value={l.dynamicId}
                        onChange={(e) => cambiarLinea(i, { dynamicId: e.target.value, premiosManuales: null })}>
                        <option value="">Elige la dinámica</option>
                        {dinamicas.map((x) => (
                          <option key={x.id} value={x.id}>{x.code} — {x.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex w-24 flex-col">
                      {d?.unidad ? etiquetaUnidad(d.unidad) : unidad}
                      <input type="number" min={1} step={1} className="rounded border px-2 py-1"
                        value={Number.isNaN(l.canjes) ? '' : l.canjes}
                        onChange={(e) => cambiarLinea(i, { canjes: e.target.valueAsNumber })} />
                    </label>
                    {lineas.length > 1 && (
                      <button type="button" onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))}
                        className="mb-1 text-xs text-red-600 underline">Quitar</button>
                    )}
                  </div>
                  {d !== undefined && !editaPremios && regla === null && (
                    <p className="mt-1 text-xs text-gray-700">
                      Premio: {sugeridos.length === 0 ? 'ninguno configurado'
                        : sugeridos.map((p) => `${nombrePremio(p.prizeId)} ×${p.units}`).join(' · ')}
                      {' · '}
                      <button type="button" className="text-blue-600 underline"
                        onClick={() => cambiarLinea(i, { premiosManuales: sugeridos })}>
                        se entregó otra cosa
                      </button>
                    </p>
                  )}
                  {editaPremios && regla === null && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {(config.premios).map((p) => (
                        <label key={p.id} className="flex items-center justify-between gap-2 text-xs">
                          {p.name}
                          <input type="number" min={0} step={1} className="w-20 rounded border px-2 py-1"
                            value={(l.premiosManuales ?? []).find((x) => x.prizeId === p.id)?.units ?? 0}
                            onChange={(e) => cambiarUnidades(i, p.id, e.target.valueAsNumber || 0)} />
                        </label>
                      ))}
                      {!eligePremio && (
                        <span className="col-span-2 text-xs text-amber-700">
                          Distinto de lo que da la dinámica: queda anotado como equivalencia por stock.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <button type="button" onClick={() => setLineas((ls) => [...ls, lineaManualVacia()])}
              className="mt-2 text-xs text-blue-600 underline">
              {regla === null ? 'Agregar otra dinámica al mismo canje' : 'Agregar otro producto al mismo canje'}
            </button>
          </>
        )}
      </section>

      <details className="mt-3 rounded border p-3">
        <summary className="cursor-pointer font-medium">Cliente, boleta y fotos (opcional)</summary>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="flex flex-col">
            Cliente
            <input className="rounded border px-2 py-1" value={cliente}
              onChange={(e) => setCliente(e.target.value)} />
          </label>
          <label className="flex flex-col">
            Contacto
            <input className="rounded border px-2 py-1" value={contacto} inputMode="tel"
              onChange={(e) => setContacto(e.target.value)} />
          </label>
          <label className="flex flex-col">
            Monto de compra (S/)
            <input className="rounded border px-2 py-1" value={monto} inputMode="decimal"
              onChange={(e) => setMonto(e.target.value)} />
          </label>
          <label className="flex flex-col">
            N.º de boleta
            <input className="rounded border px-2 py-1" value={numeroBoleta}
              autoCapitalize="characters" autoComplete="off" spellCheck={false}
              onChange={(e) => setNumeroBoleta(e.target.value)} />
          </label>
          <label className="col-span-2 flex flex-col">
            Observaciones
            <textarea rows={2} className="rounded border px-2 py-1" value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)} />
          </label>
          {/* Galería y no solo cámara, como en la corrección: en la oficina la foto llega por
              WhatsApp o desde la computadora. */}
          <label className="flex flex-col">
            Foto de boleta
            <input key={`b${claveFotos}`} type="file" accept="image/*" className="text-xs"
              onChange={(e) => elegirFoto('boleta', e.target.files?.[0] ?? null)} />
          </label>
          <label className="flex flex-col">
            Foto de premio
            <input key={`p${claveFotos}`} type="file" accept="image/*" className="text-xs"
              onChange={(e) => elegirFoto('premio', e.target.files?.[0] ?? null)} />
          </label>
        </div>
      </details>

      <label className="mt-3 flex flex-col">
        Motivo de la carga <span className="text-xs text-gray-500">(obligatorio)</span>
        <textarea rows={2} className="rounded border px-2 py-1" value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ej.: regularización de la planilla en papel del 30-oct" />
      </label>

      <p className="mt-2 text-xs text-gray-500">
        Si lo cargas después del día del canje, queda marcado «registro tardío», y si la persona no
        marcó ingreso ese día, «sin asistencia». En una regularización es lo esperado: dice cuándo se
        cargó de verdad.
      </p>

      {error !== '' && (
        <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          {error}
        </p>
      )}
      {hecho !== '' && (
        <p role="status" className="mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-green-900">
          {hecho}
        </p>
      )}

      <div className="mt-4 flex items-center justify-end gap-3">
        {bloqueo !== null && hecho === '' && <span className="text-xs text-gray-600">{bloqueo}</span>}
        <button type="button" onClick={onCerrar} className="rounded border px-3 py-1.5">
          {cargados > 0 ? 'Terminar' : 'Cancelar'}
        </button>
        <button type="button" onClick={() => void guardar()}
          disabled={guardando || bloqueo !== null}
          className="rounded bg-blue-600 px-3 py-1.5 text-white disabled:opacity-50">
          {guardando ? 'Guardando…' : 'Guardar canje'}
        </button>
      </div>
    </div>
  );
}
